import { API } from "config/api";
import { API_ROUTES } from "config/apiRoutes";
import {
  AccountGroup,
  CandidateFamily,
  GroupableProfile,
} from "types/accountGroup";

function emailFamilyKey(email: string): string {
  const normalized = email.trim().toLowerCase();
  const separator = normalized.lastIndexOf("@");
  if (separator <= 0) {
    return normalized;
  }
  const local = normalized.slice(0, separator);
  const domain = normalized.slice(separator + 1);
  return `${local.split("+", 1)[0]}@${domain}`;
}

function profileArray(value: unknown): GroupableProfile[] {
  if (!Array.isArray(value)) return [];
  return value.map((profile: any) => ({
    ...profile,
    group_id:
      profile.group_id ??
      profile.account_group_id ??
      profile.accountGroupId ??
      null,
    is_grouped:
      profile.is_grouped ??
      Boolean(
        profile.group_id ??
          profile.account_group_id ??
          profile.accountGroupId,
      ),
  }));
}

function normalizeGroup(value: any): AccountGroup {
  return {
    id: String(value.id ?? value.group_id ?? value.account_group_id),
    members: profileArray(value.members ?? value.users ?? value.profiles),
  };
}

function isActionableFamily(profiles: GroupableProfile[]): boolean {
  const ungroupedCount = profiles.filter((profile) => !profile.group_id).length;
  if (ungroupedCount > 1) {
    return true;
  }
  return (
    ungroupedCount > 0 && profiles.some((profile) => Boolean(profile.group_id))
  );
}

function existingGroupId(profiles: GroupableProfile[]): string | null {
  const groupIds = Array.from(
    new Set(
      profiles
        .map((profile) => profile.group_id)
        .filter((groupId): groupId is string => Boolean(groupId)),
    ),
  );
  return groupIds.length === 1 ? groupIds[0] : null;
}

export async function listAccountGroups(): Promise<AccountGroup[]> {
  const { data } = await API.get(API_ROUTES.ACCOUNT_GROUPS.LIST());
  const groups = Array.isArray(data) ? data : data?.groups || [];
  return groups.map(normalizeGroup);
}

export async function lookupGroupCandidates(
  email?: string,
): Promise<CandidateFamily[]> {
  const { data } = await API.get(API_ROUTES.ACCOUNT_GROUPS.CANDIDATES(), {
    params: email ? { email } : undefined,
  });
  const families = data?.families;
  if (Array.isArray(families)) {
    return families
      .map((family: any, index: number) => {
        const profiles = profileArray(
          family.profiles ?? family.members ?? family.users ?? family.candidates,
        );
        return {
          id: String(
            family.id ??
              family.family_id ??
              family.family ??
              family.email ??
              `family-${index}`,
          ),
          email: family.email ?? family.family,
          existing_group_id:
            family.existing_group_id ??
            family.existingGroupId ??
            existingGroupId(profiles),
          profiles,
        };
      })
      .filter((family) => isActionableFamily(family.profiles));
  }

  const candidates = profileArray(
    Array.isArray(data) ? data : data?.candidates ?? data?.users,
  );
  const grouped = new Map<string, GroupableProfile[]>();
  candidates.forEach((profile) => {
    const key =
      profile.family_id ||
      profile.family_key ||
      emailFamilyKey(profile.email);
    grouped.set(key, [...(grouped.get(key) || []), profile]);
  });
  return Array.from(grouped, ([id, profiles]) => ({
    id,
    email: profiles[0]?.email,
    existing_group_id: existingGroupId(profiles),
    profiles,
  })).filter((family) => isActionableFamily(family.profiles));
}

export async function createAccountGroup(
  userIds: string[],
): Promise<AccountGroup> {
  const { data } = await API.post(API_ROUTES.ACCOUNT_GROUPS.LIST(), {
    user_ids: userIds,
  });
  return normalizeGroup(data.group ?? data);
}

export async function updateAccountGroup(
  id: string,
  userIds: string[],
): Promise<AccountGroup> {
  const { data } = await API.patch(API_ROUTES.ACCOUNT_GROUPS.DETAIL(id), {
    user_ids: userIds,
  });
  return normalizeGroup(data.group ?? data);
}

export async function deleteAccountGroup(id: string): Promise<void> {
  await API.delete(API_ROUTES.ACCOUNT_GROUPS.DETAIL(id));
}
